import { z } from "zod";

import { TalentAssessmentEnum } from "../enum/reviewEnums";

const quickReviewSchema = z.object({
  whoIsReviewing: z.literal("ENTITY_ADMIN"),
  overallFitScore: z.number(),
  talentAssessment: z.nativeEnum(TalentAssessmentEnum, { required_error: "TALENT_ASSESSMENT_REQUIRED" }),
});

export const skillRatingSchema = z.object(
  {
    rating: z.number({ required_error: "RATING_REQUIRED" }),
    notes: z.string().optional(),
  },
  { required_error: "RATING_REQUIRED" }
);

const talentTechnicalSkillsSchema = z.object({
  firstTouchReceiving: skillRatingSchema,
  passingRangeAccuracy: skillRatingSchema,
  dribbling1v1Ability: skillRatingSchema,
  shootingFinishing: skillRatingSchema,
  crossingDelivery: skillRatingSchema,
  headingTechnique: skillRatingSchema,
});

const talentTacticalAwarenessSchema = z.object({
  positioning: skillRatingSchema,
  offTheBallMovement: skillRatingSchema,
  defensiveAwareness: skillRatingSchema,
  transitionPlay: skillRatingSchema,
  spatialAwareness: skillRatingSchema,
  tacticalAdaptability: skillRatingSchema,
});

const talentAthleticismPhysicalSchema = z.object({
  acceleration: skillRatingSchema,
  topSpeed: skillRatingSchema,
  strength: skillRatingSchema,
  endurance: skillRatingSchema,
  agility: skillRatingSchema,
  flexibilityCoordination: skillRatingSchema,
});

const talentMindsetPsychologicalSchema = z.object({
  concentration: skillRatingSchema,
  confidence: skillRatingSchema,
  leadership: skillRatingSchema,
  discipline: skillRatingSchema,
  workRate: skillRatingSchema,
  resilience: skillRatingSchema,
  coachAbility: skillRatingSchema,
  professionalism: skillRatingSchema,
});

const finalNarrativeTagsSchema = z.object({
  ginoSummary: z.string({ required_error: "GINO_SUMMARY_REQUIRED" }),
  tags: z.array(z.string()).optional(),
});

export const addTalentReviewSchema = z.object({
  ...quickReviewSchema.shape,
  ...talentTechnicalSkillsSchema.shape,
  ...talentTacticalAwarenessSchema.shape,
  ...talentAthleticismPhysicalSchema.shape,
  ...talentMindsetPsychologicalSchema.shape,
  ...finalNarrativeTagsSchema.shape,
});

const professionalExpertiseSchema = z.object({
  domainKnowledge: skillRatingSchema,
  trackRecordAchievements: skillRatingSchema,
  analyticalSkills: skillRatingSchema,
  talentIdentification: skillRatingSchema,
  innovationMethods: skillRatingSchema,
});

const communicationLeadershipSchema = z.object({
  leadership: skillRatingSchema,
  communicationClarity: skillRatingSchema,
  teamManagement: skillRatingSchema,
  influenceNetworking: skillRatingSchema,
  culturalAdaptability: skillRatingSchema,
});

const professionalismWorkEthicSchema = z.object({
  discipline: skillRatingSchema,
  workRate: skillRatingSchema,
  integrityEthics: skillRatingSchema,
  resilience: skillRatingSchema,
  coachAbility: skillRatingSchema,
  professionalPresence: skillRatingSchema,
});

const developmentGrowthSchema = z.object({
  learningOrientation: skillRatingSchema,
  adaptability: skillRatingSchema,
  futurePotential: skillRatingSchema,
  specializationFit: skillRatingSchema,
});

export const addProfessionalReviewSchema = z.object({
  ...quickReviewSchema.shape,
  ...professionalExpertiseSchema.shape,
  ...communicationLeadershipSchema.shape,
  ...professionalismWorkEthicSchema.shape,
  ...developmentGrowthSchema.shape,
  ...finalNarrativeTagsSchema.shape,
});
